import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// "Put me back where I was in the list."
//
// Client, League Play Dev Request, on the standalone leaderboard's back arrow:
//
//   "My Leagues > view all > completed > view results > leaderboard preview >
//    back arrow > returns you to the 'Live now' screen. You should go back to
//    the 'Completed' tab you came from. Please program it so the back arrow
//    takes you back to where you came from, and, let's assume one day this is a
//    long list, it takes you back to the same place in the list. The same
//    completed competition you originally clicked on."
//
// Two separate things, and they are fixed in two separate places:
//
//   • WHICH TAB is the URL's job. MyLeagues now keeps `?tab=<key>` in the
//     address while you are on a tab, so the history entry the back arrow
//     returns to already names the Completed tab. That needs nothing from this
//     file.
//   • WHERE IN THE LIST is this file. History has no idea, because the list is
//     fetched after the screen mounts — by the time the rows exist, any scroll
//     the browser restored has already been applied to a page that was still
//     empty.
//
// So the card that was opened records ITSELF, and the screen scrolls that exact
// card back into view once it has rendered. Anchoring to the card rather than to
// a pixel offset is what makes the client's "the same completed competition you
// originally clicked on" survive a list that has since gained or lost a league.
//
// sessionStorage, not localStorage: this is a fact about one tab's current
// journey, and it should not still be waiting a week later in a new session.
// Every access is guarded — a private window can throw on both read and write.
const KEY = "qushi:listPosition";

const read = () => {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const clear = () => {
  try {
    sessionStorage.removeItem(KEY);
  } catch (e) {
    /* nothing to do — the restore simply does not happen */
  }
};

/**
 * Called by a row or card as it is opened. `path` is the list screen's own
 * pathname, so a position saved on My Leagues can never be applied to League
 * Play home.
 */
export const rememberListPosition = (path, id) => {
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({
        path,
        id: id == null ? null : String(id),
        // The fallback, for when the remembered card is no longer in the list.
        y: typeof window !== "undefined" ? window.scrollY : 0,
      })
    );
  } catch (e) {
    /* a position we cannot save is a position we do not restore */
  }
};

/**
 * Called by the list screen. Scrolls the remembered card back into view once
 * `ready` turns true — i.e. once the rows are actually on the page.
 *
 * Restores ONCE per visit and then forgets, so a later reload or a fresh visit
 * to the same screen opens at the top the way it always has.
 */
export const useRestoreListPosition = (ready) => {
  const { pathname } = useLocation();
  const done = useRef(false);

  useEffect(() => {
    if (done.current || !ready) return;
    const saved = read();
    if (!saved || saved.path !== pathname) return;
    done.current = true;
    clear();

    // One frame, so the scroll runs against the rows that have just been
    // committed rather than against the layout they are replacing.
    const raf = requestAnimationFrame(() => {
      const card = saved.id
        ? document.querySelector(`[data-list-item="${CSS.escape(saved.id)}"]`)
        : null;
      if (card && typeof card.scrollIntoView === "function") {
        card.scrollIntoView({ block: "center" });
        return;
      }
      if (typeof saved.y === "number") window.scrollTo(0, saved.y);
    });
    return () => cancelAnimationFrame(raf);
  }, [ready, pathname]);
};
